import type { Messages } from '../index';

export const common: Messages['common'] = {
  brand: 'immoOS',
  'sign-off': 'Best regards,\nYour immoOS team',
  copy: {
    'aria-label': 'Copy {subject}',
    success: '{subject} copied to clipboard.',
    subjects: {
      email: 'Email address',
      phone: 'Phone number',
    },
  },
  'release-notice': {
    title: 'A new version is available',
    description: 'Reload the page to use the latest version of immoOS.',
    action: 'Reload',
  },
  'outdated-view': {
    title: 'This view is no longer up to date',
    action: 'Reload',
  },
  modal: {
    close: 'Close',
    'confirm-close': {
      title: 'Discard changes?',
      description: 'If you close now, your unsaved changes will be lost.',
      confirm: 'Discard',
      cancel: 'Keep editing',
    },
  },
  immobilientyp: {
    category: {
      APARTMENT: 'Apartment',
      HOUSE: 'House',
      LAND: 'Land',
      COMMERCIAL: 'Commercial',
      GARAGE_PARKING: 'Garage / Parking',
    },
    subtype: {
      FLOOR_APARTMENT: 'Floor apartment',
      GROUND_FLOOR_APARTMENT: 'Ground-floor apartment',
      ATTIC_APARTMENT: 'Attic apartment',
      PENTHOUSE: 'Penthouse',
      MAISONETTE: 'Maisonette',
      LOFT: 'Loft',
      BASEMENT_APARTMENT: 'Basement apartment',
      STUDIO_APARTMENT: 'Studio apartment',
      DETACHED_HOUSE: 'Detached house',
      SEMI_DETACHED_HOUSE: 'Semi-detached house',
      TERRACED_HOUSE: 'Terraced house',
      VILLA: 'Villa',
      BUNGALOW: 'Bungalow',
      TOWNHOUSE: 'Townhouse',
      MULTI_FAMILY_HOUSE: 'Multi-family house',
      RESIDENTIAL_LAND: 'Residential',
      COMMERCIAL_LAND: 'Commercial',
      AGRICULTURAL_LAND: 'Agricultural / forestry',
      OFFICE_PRACTICE: 'Office / practice',
      RETAIL: 'Retail',
      HOSPITALITY: 'Hospitality',
      HOTEL: 'Hotel',
      INDUSTRIAL: 'Warehouse / logistics / production',
      SPECIAL_PURPOSE: 'Special-purpose property',
      GARAGE: 'Garage',
      UNDERGROUND_PARKING: 'Underground parking space',
      OUTDOOR_PARKING: 'Outdoor parking space',
      CARPORT: 'Carport',
      DUPLEX_PARKING: 'Duplex',
    },
    'commercial-fine-type': {
      LOFT: 'Loft',
      STUDIO: 'Studio',
      OFFICE: 'Office',
      OFFICE_FLOOR: 'Office floor',
      OFFICE_BUILDING: 'Office building',
      OFFICE_CENTRE: 'Office centre',
      OFFICE_STORAGE_BUILDING: 'Office and storage building',
      SURGERY: 'Practice',
      SURGERY_FLOOR: 'Practice floor',
      SURGERY_BUILDING: 'Practice building',
      COMMERCIAL_CENTRE: 'Commercial centre',
      LIVING_AND_COMMERCIAL_BUILDING: 'Residential and commercial building',
      OFFICE_AND_COMMERCIAL_BUILDING: 'Office and commercial building',
      BAR_LOUNGE: 'Bar/lounge',
      CAFE: 'Café',
      CLUB_DISCO: 'Club/discotheque',
      GUESTS_HOUSE: 'Guest house',
      TAVERN: 'Tavern',
      HOTEL: 'Hotel',
      HOTEL_RESIDENCE: 'Hotel estate',
      HOTEL_GARNI: 'Hotel garni',
      PENSION: 'Guesthouse (Pension)',
      RESTAURANT: 'Restaurant',
      BUNGALOW: 'Holiday bungalow',
      SHOWROOM_SPACE: 'Showroom space',
      HALL: 'Hall',
      HIGH_LACK_STORAGE: 'High-bay warehouse',
      INDUSTRY_HALL: 'Industrial hall',
      INDUSTRY_HALL_WITH_OPEN_AREA: 'Industrial hall with open space',
      COLD_STORAGE: 'Cold store',
      MULTIDECK_CABINET_STORAGE: 'Refrigerated rack storage',
      STORAGE_WITH_OPEN_AREA: 'Storage with open space',
      STORAGE_AREA: 'Storage space',
      STORAGE_HALL: 'Storage hall',
      SERVICE_AREA: 'Service space',
      SHIPPING_STORAGE: 'Forwarding warehouse',
      REPAIR_SHOP: 'Workshop',
      RESIDENCE: 'Estate',
      FARM: 'Farm',
      HORSE_FARM: 'Equestrian centre',
      VINEYARD: 'Vineyard',
      LEISURE_FACILITY: 'Leisure facility',
      INDUSTRIAL_AREA: 'Commercial space',
      SPECIAL_ESTATE: 'Special property',
    },
  },
  'email-templates': {
    areas: {
      BUYER: 'Buyer',
      OWNER: 'Owner',
      GENERAL: 'General',
    },
    placeholders: {
      RECIPIENT_GREETING: 'Salutation line',
      RECIPIENT_SALUTATION: 'Salutation',
      RECIPIENT_FIRST_NAME: 'First name',
      RECIPIENT_LAST_NAME: 'Last name',
      PROPERTY_TITLE: 'Property title',
      PROPERTY_LOCATION: 'Property location',
      PROPERTY_ADDRESS: 'Property address',
      PROPERTY_PRICE: 'Purchase price',
      PROPERTY_NUMBER: 'Property no.',
      PROSPECT_OFFER: 'Offer',
      PROSPECT_NOTARY_APPOINTMENT: 'Notary appointment',
      PROSPECT_PERSONAL_LINK: 'Link to the personal page',
      SENDER_FIRST_NAME: 'Sender first name',
      SENDER_LAST_NAME: 'Sender last name',
      SENDER_PHONE: 'Sender phone',
      SENDER_EMAIL: 'Sender e-mail',
      COMPANY_NAME: 'Company name',
    },
    'placeholder-groups': {
      RECIPIENT: 'Recipient',
      PROPERTY: 'Property',
      PROSPECT: 'Prospect',
      SENDER: 'Sender',
      COMPANY: 'Company',
    },
  },
  'buyer-contract-templates': {
    'standard-name': 'Buyer brokerage agreement with withdrawal notice – Standard',
    category: 'Buyer brokerage agreement',
    blocks: {
      contract: 'Brokerage agreement',
      withdrawalNotice: 'Withdrawal notice',
      withdrawalForm: 'Model withdrawal form',
    },
    groups: {
      COMPANY: 'Company',
      BUYER: 'Buyer',
      PROPERTY: 'Property',
      NOTICE: 'Notice',
      CONTRACT: 'Agreement',
    },
    fields: {
      companyName: 'Company name',
      companyAddress: 'Address',
      companyEmail: 'E-mail',
      companyPhone: 'Phone',
      buyerName: 'Name',
      commission: 'Commission',
      commissionNote: 'Commission note',
      propertyTitle: 'Title',
      propertyCity: 'City',
      noticeSentOn: 'Sent on',
      concludedOn: 'Concluded on',
      withdrawalPeriodEndsOn: 'Withdrawal period ends',
    },
    active: 'Active',
    'created-by-immoos': 'immoOS',
  },
};
